import { Controller } from '@hotwired/stimulus';

type WorkoutDraftSet = {
    exercise_name?: unknown;
    success_measure?: unknown;
    notes?: unknown;
    repeats?: unknown;
};

export class WorkoutBuilderController extends Controller<HTMLElement> {
    static targets = ['sets', 'template', 'importWarning'];
    static values = { draft: Object, collapseInitial: Boolean };
    declare readonly setsTarget: HTMLElement;
    declare readonly templateTarget: HTMLTemplateElement;
    declare readonly importWarningTarget: HTMLElement;
    declare readonly hasImportWarningTarget: boolean;
    declare readonly draftValue: { sets?: WorkoutDraftSet[] };
    declare readonly hasDraftValue: boolean;
    declare readonly collapseInitialValue: boolean;
    declare readonly hasCollapseInitialValue: boolean;

    private dragExercise: HTMLElement | null = null;
    private dragPlaceholder: HTMLDivElement | null = null;
    private dragPointerId: number | null = null;
    private dragPointerOffsetY = 0;
    private readonly handleDragMoveBound = (event: PointerEvent): void => this.handleDragMove(event);
    private readonly handleDragEndBound = (event: PointerEvent): void => this.handleDragEnd(event);

    connect(): void {
        if (this.setElements().length === 0) {
            const draftSets = this.hasDraftValue && Array.isArray(this.draftValue.sets)
                ? this.draftValue.sets
                : [];
            if (draftSets.length > 0) this.loadDraft(draftSets);
            else this.addSet();
        }
        this.renumber();
        if (this.hasCollapseInitialValue && this.collapseInitialValue) {
            this.setElements().forEach(set => this.collapseSet(set));
        }
        this.updateImportWarning();
    }

    disconnect(): void {
        this.teardownDragState();
    }

    addSet(event?: Event): void {
        event?.preventDefault();
        if (this.setElements().length >= 50) return;
        if (event) {
            const current = this.setElements().at(-1);
            if (current && !this.commitSet(current)) return;
        }
        this.setElements().forEach(set => this.collapseSet(set));
        const set = this.createSet();
        if (!set) return;
        this.setsTarget.appendChild(set);
        this.addRepeatToSet(set);
        this.renumber();
    }

    markSetDirty(event: Event): void {
        const set = (event.target as HTMLElement)
            .closest<HTMLElement>('[data-workout-builder-target="set"]');
        if (!set) return;
        set.dataset.saved = 'false';
        set.querySelector<HTMLElement>('[data-saved-indicator]')?.classList.add('hidden');
    }

    removeSet(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget as HTMLElement;
        button.closest<HTMLElement>('[data-workout-builder-target="set"]')?.remove();
        if (this.setElements().length === 0) this.addSet();
        this.renumber();
    }

    toggleSet(event: Event): void {
        event.preventDefault();
        const set = (event.currentTarget as HTMLElement)
            .closest<HTMLElement>('[data-workout-builder-target="set"]');
        if (!set) return;
        if (set.dataset.collapsed !== 'true') {
            if (this.commitSet(set)) this.collapseSet(set);
            return;
        }
        set.dataset.collapsed = 'false';
        this.toggleSetBody(set, false);
        set.querySelector<HTMLElement>('[data-set-number]')?.classList.remove('hidden');
        set.querySelector<HTMLElement>('[data-exercise-summary]')?.classList.add('hidden');
        if (set.dataset.saved === 'true') {
            const savedIndicator = set.querySelector<HTMLElement>('[data-saved-indicator]');
            savedIndicator?.classList.remove('hidden');
            if (savedIndicator) savedIndicator.hidden = false;
        }
        set.querySelector<HTMLElement>('[data-collapse-chevron]')?.classList.add('rotate-180');
    }

    beginExerciseDrag(event: PointerEvent): void {
        if (event.button !== 0 || this.dragExercise) return;

        event.preventDefault();
        event.stopPropagation();
        const handle = event.currentTarget as HTMLElement | null;
        const exercise = handle?.closest<HTMLElement>('[data-workout-builder-target="set"]');
        if (!handle || !exercise) return;

        const rect = exercise.getBoundingClientRect();
        const placeholder = document.createElement('div');
        placeholder.className = 'workout-builder-exercise-placeholder rounded-2xl border-2 border-dashed border-green-300 bg-green-50/70';
        placeholder.style.height = `${rect.height}px`;

        this.dragExercise = exercise;
        this.dragPlaceholder = placeholder;
        this.dragPointerId = event.pointerId;
        this.dragPointerOffsetY = event.clientY - rect.top;

        exercise.after(placeholder);
        exercise.classList.add('workout-builder-exercise--dragging');
        exercise.style.width = `${rect.width}px`;
        exercise.style.height = `${rect.height}px`;
        exercise.style.left = `${rect.left}px`;
        exercise.style.top = `${rect.top}px`;
        this.setsTarget.classList.add('workout-builder-exercises--sorting');
        document.body.classList.add('workout-builder-drag-active');
        handle.setPointerCapture?.(event.pointerId);

        document.addEventListener('pointermove', this.handleDragMoveBound);
        document.addEventListener('pointerup', this.handleDragEndBound);
        document.addEventListener('pointercancel', this.handleDragEndBound);
    }

    stopExerciseDragClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    addRepeat(event: Event): void {
        event.preventDefault();
        const set = (event.currentTarget as HTMLElement)
            .closest<HTMLElement>('[data-workout-builder-target="set"]');
        if (set) this.addRepeatToSet(set);
    }

    removeRepeat(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        const set = button.closest<HTMLElement>('[data-workout-builder-target="set"]');
        button.closest<HTMLElement>('[data-workout-repeat]')?.remove();
        if (set && this.repeatElements(set).length === 0) this.addRepeatToSet(set);
        this.renumber();
    }

    clearMissing(event: Event): void {
        const field = event.currentTarget as HTMLInputElement | HTMLSelectElement;
        if (!field.value.trim()) return;
        field.classList.remove('border-red-500', 'bg-red-50', 'ring-1', 'ring-red-300');
        delete field.dataset.importMissing;
        this.updateImportWarning();
    }

    successMeasureChanged(event: Event): void {
        const select = event.currentTarget as HTMLSelectElement;
        const exercise = select.closest<HTMLElement>('[data-workout-builder-target="set"]');
        if (exercise) this.updateMetricLabels(exercise, select.value);
    }

    private handleDragMove(event: PointerEvent): void {
        if (!this.dragExercise || !this.dragPlaceholder || this.dragPointerId !== event.pointerId) return;

        event.preventDefault();
        this.dragExercise.style.top = `${event.clientY - this.dragPointerOffsetY}px`;

        const exercises = this.setElements().filter(exercise => exercise !== this.dragExercise);
        for (const exercise of exercises) {
            const rect = exercise.getBoundingClientRect();
            if (event.clientY < rect.top + (rect.height / 2)) {
                exercise.before(this.dragPlaceholder);
                return;
            }
        }
        this.setsTarget.append(this.dragPlaceholder);
    }

    private handleDragEnd(event: PointerEvent): void {
        if (this.dragPointerId !== event.pointerId) return;

        if (this.dragExercise && this.dragPlaceholder) {
            this.dragPlaceholder.before(this.dragExercise);
        }
        this.teardownDragState();
        this.renumber();
    }

    private teardownDragState(): void {
        document.removeEventListener('pointermove', this.handleDragMoveBound);
        document.removeEventListener('pointerup', this.handleDragEndBound);
        document.removeEventListener('pointercancel', this.handleDragEndBound);
        document.body.classList.remove('workout-builder-drag-active');
        this.setsTarget?.classList.remove('workout-builder-exercises--sorting');

        if (this.dragExercise) {
            this.dragExercise.classList.remove('workout-builder-exercise--dragging');
            this.dragExercise.style.removeProperty('width');
            this.dragExercise.style.removeProperty('height');
            this.dragExercise.style.removeProperty('left');
            this.dragExercise.style.removeProperty('top');
        }
        this.dragPlaceholder?.remove();
        this.dragExercise = null;
        this.dragPlaceholder = null;
        this.dragPointerId = null;
        this.dragPointerOffsetY = 0;
    }

    private createSet(): HTMLElement | null {
        const set = this.templateTarget.content.firstElementChild?.cloneNode(true);
        if (!(set instanceof HTMLElement)) return null;
        const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const setKey = set.querySelector<HTMLInputElement>('input[name="set_key"]');
        if (setKey) setKey.value = key;
        return set;
    }

    private addRepeatToSet(set: HTMLElement): HTMLElement | null {
        if (this.repeatElements(set).length >= 100) return null;
        const template = set.querySelector<HTMLTemplateElement>('[data-repeat-template]');
        const container = set.querySelector<HTMLElement>('[data-repeats]');
        const repeat = template?.content.firstElementChild?.cloneNode(true);
        if (!(repeat instanceof HTMLElement) || !container) return null;
        const setKey = set.querySelector<HTMLInputElement>('input[name="set_key"]')?.value ?? '';
        const repeatSetKey = repeat.querySelector<HTMLInputElement>('input[name="repeat_set_key"]');
        if (repeatSetKey) repeatSetKey.value = setKey;
        container.appendChild(repeat);
        this.renumber();
        return repeat;
    }

    private loadDraft(draftSets: WorkoutDraftSet[]): void {
        draftSets.slice(0, 50).forEach(draftSet => {
            const set = this.createSet();
            if (!set) return;
            this.setsTarget.appendChild(set);
            this.fillRequiredField(set.querySelector('input[name="set_exercise"]'), draftSet.exercise_name);
            this.fillRequiredField(set.querySelector('select[name="set_measure"]'), draftSet.success_measure);
            const notes = set.querySelector<HTMLInputElement>('input[name="set_notes"]');
            if (notes && typeof draftSet.notes === 'string') notes.value = draftSet.notes;
            const repeats = Array.isArray(draftSet.repeats) && draftSet.repeats.length > 0
                ? draftSet.repeats.slice(0, 100)
                : [{ target_value: null, target_unit: null }];
            repeats.forEach(rawRepeat => {
                const repeat = this.addRepeatToSet(set);
                if (!repeat || typeof rawRepeat !== 'object' || rawRepeat === null) return;
                const data = rawRepeat as Record<string, unknown>;
                this.fillOptionalField(repeat.querySelector('input[name="repeat_target"]'), data.target_value);
                this.fillOptionalField(repeat.querySelector('input[name="repeat_unit"]'), data.target_unit);
                this.fillOptionalField(repeat.querySelector('input[name="repeat_repetitions"]'), data.target_repetitions);
            });
            this.updateMetricLabels(set, String(draftSet.success_measure ?? ''));
        });
        this.renumber();
    }

    private fillRequiredField(
        field: HTMLInputElement | HTMLSelectElement | null,
        value: unknown,
    ): void {
        if (!field) return;
        if (value !== null && value !== undefined && String(value).trim()) {
            field.value = String(value);
            if (field.value) return;
        }
        field.value = '';
        field.dataset.importMissing = 'true';
        field.classList.add('border-red-500', 'bg-red-50', 'ring-1', 'ring-red-300');
    }

    private fillOptionalField(field: HTMLInputElement | null, value: unknown): void {
        if (field && value !== null && value !== undefined && String(value).trim()) {
            field.value = String(value);
        }
    }

    private updateImportWarning(): void {
        if (!this.hasImportWarningTarget) return;
        this.importWarningTarget.hidden = !this.element.querySelector('[data-import-missing="true"]');
    }

    private renumber(): void {
        this.setElements().forEach((set, index) => {
            const number = set.querySelector<HTMLElement>('[data-set-number]');
            if (number) number.textContent = `Exercise ${index + 1}`;
            this.repeatElements(set).forEach((repeat, repeatIndex) => {
                const number = repeat.querySelector<HTMLElement>('[data-repeat-number]');
                if (number) number.textContent = `Set ${repeatIndex + 1}`;
            });
        });
    }

    private repeatElements(set: HTMLElement): HTMLElement[] {
        return Array.from(set.querySelectorAll<HTMLElement>('[data-repeats] > [data-workout-repeat]'));
    }

    private setElements(): HTMLElement[] {
        return Array.from(
            this.setsTarget.querySelectorAll<HTMLElement>(':scope > [data-workout-builder-target="set"]'),
        );
    }

    private updateMetricLabels(exercise: HTMLElement, measure: string): void {
        const labels: Record<string, string> = {
            strength: 'Weight target',
            muscle_size: 'Measurement target',
            repetitions: 'Rep target',
            time: 'Time target',
            distance: 'Distance target',
            pace: 'Pace / speed target',
            power: 'Power target',
            calories: 'Calorie target',
            effort: 'Effort target',
        };
        exercise.querySelectorAll<HTMLElement>('[data-metric-label]').forEach(label => {
            label.textContent = labels[measure] ?? 'Metric target';
        });
    }

    private collapseSet(set: HTMLElement): void {
        const exerciseName = set.querySelector<HTMLInputElement>('input[name="set_exercise"]')?.value.trim();
        if (!exerciseName) return;
        const summary = set.querySelector<HTMLElement>('[data-exercise-summary]');
        if (summary) {
            summary.textContent = exerciseName;
            summary.classList.remove('hidden');
        }
        set.querySelector<HTMLElement>('[data-set-number]')?.classList.add('hidden');
        const savedIndicator = set.querySelector<HTMLElement>('[data-saved-indicator]');
        savedIndicator?.classList.add('hidden');
        if (savedIndicator) savedIndicator.hidden = true;
        set.dataset.collapsed = 'true';
        this.toggleSetBody(set, true);
        set.querySelector<HTMLElement>('[data-collapse-chevron]')?.classList.remove('rotate-180');
    }

    private commitSet(set: HTMLElement): boolean {
        const fields = Array.from(
            set.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input:not([type="hidden"]), select'),
        );
        const invalid = fields.find(field => !field.checkValidity());
        if (invalid) {
            invalid.reportValidity();
            invalid.focus();
            return false;
        }
        set.dataset.saved = 'true';
        set.querySelector<HTMLElement>('[data-saved-indicator]')?.classList.remove('hidden');
        return true;
    }

    private toggleSetBody(set: HTMLElement, hidden: boolean): void {
        Array.from(set.children).forEach(child => {
            if (!(child instanceof HTMLElement)) return;
            if (child.matches('input[type="hidden"], [data-exercise-header]')) return;
            child.hidden = hidden;
            child.classList.toggle('hidden', hidden);
        });
    }
}
